<template>
  <div aria-live="polite">
    <DisplayTemplate
      v-if="template && !identityUI"
      :template="template"
      @buttonClicked="buttonCmd"
    />
    <ErrorComponent v-else-if="error && !identityUI" :error="error" />
    <LoadingComponent
      v-else-if="!identityUI && !error && !template"
      :large-spinner="true"
    ></LoadingComponent>
  </div>
</template>

<script setup lang="ts">
import {
  ButtonAction,
  ButtonNextAction,
  InputAnswer,
  UIContentJSON,
  UIPage,
} from "@/models/template";
import SDK, {
  IChallenge,
  ICustomerAuthnConfig,
  IIdentifyCustomer,
  INotify,
  ISetAuthenticationFactor,
  securityMode,
} from "@entersekt/customerauthn-sdk-web";
import { ref, watch } from "vue";
import { useRoute } from "vue-router";
import DisplayTemplate from "../components/DisplayTemplate.vue";
import ErrorComponent from "../components/ErrorComponent.vue";
import LoadingComponent from "../components/LoadingComponent.vue";
import { config as env } from "../config";

const emit = defineEmits<{
  setBackgroundColor: [color: string];
}>();

const route = useRoute();
const shortname = route.params.channelShortname as string;
const pathCode = route.params.joinCode;
const queryCode = route.query.code?.toString();
const postToParent =
  route.query.post_to_parent?.toString() === "true" ? true : false;
const redirectURI = route.query.redirect_uri?.toString() || "";

const code = pathCode ? pathCode : queryCode;

const template = ref<UIContentJSON>();

watch(template, (newValue, oldValue) => {
  if (
    newValue?.theme?.pageBackgroundColor &&
    oldValue?.theme?.pageBackgroundColor !==
      newValue?.theme?.pageBackgroundColor
  ) {
    emit("setBackgroundColor", newValue?.theme?.pageBackgroundColor);
  }
  if (newValue?.summary) {
    document.title = newValue.summary;
  }
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const interaction = ref<any>(null);
const error = ref("");
const identityUI = ref(false);

const buttonCmd = (
  buttonRole: ButtonAction,
  inputs?: InputAnswer[],
  actionNext?: ButtonNextAction,
) => {
  if (buttonRole === "CONTINUE") {
    interaction.value.proceed(inputs, actionNext);
  } else if (buttonRole === "CANCEL" || buttonRole === "CANCEL_AND") {
    if (postToParent) {
      const result = {
        status: "REJECTED",
      };
      window.parent.postMessage(result, redirectURI);
    }
    interaction.value.cancel(actionNext);
  }
};

if (!code) {
  error.value = "No Code Provided";
} else {
  const config: ICustomerAuthnConfig = SDK.configBuilder
    .setConnectionURL(env.browserChannelURL || "")
    .setTokenEndpointURL(env.endpointAuthenticatorURL || "")
    .setSignalCollectorURL(env.signalCollectorURL)
    .setChannelShortName(shortname || "EHS")
    .setTimeoutInMilliseconds(10000)
    .setRedirectDelayInMilliseconds(2000)
    .setSecurityProviderConfig({ mode: env.securityMode as securityMode })
    .build();

  SDK.init(config)
    .then((sdk) => {
      sdk
        .onSetAuthenticationFactor()
        .subscribe((result: ISetAuthenticationFactor) => {
          interaction.value = result;
          if (result.uiContent && "contentJSON" in result.uiContent) {
            template.value = (result.uiContent as UIPage).contentJSON;
          } else {
            template.value = result.uiContent as UIContentJSON;
          }
        });

      sdk.onChallengeCustomer().subscribe((result: IChallenge) => {
        interaction.value = result;
        if (result.uiContent && "contentJSON" in result.uiContent) {
          template.value = (result.uiContent as UIPage).contentJSON;
        } else {
          template.value = result.uiContent as UIContentJSON;
        }
      });

      sdk.onNotify().subscribe((result: INotify) => {
        interaction.value = result;
        if (result.uiContent && "contentJSON" in result.uiContent) {
          template.value = (result.uiContent as UIPage).contentJSON;
        } else {
          template.value = result.uiContent as UIContentJSON;
        }
      });

      sdk
        .onIdentifyCustomer()
        .subscribe(async (identifyCustomer: IIdentifyCustomer) => {
          identityUI.value = true;
          await identifyCustomer.proceed();
          identityUI.value = false;
        });

      sdk
        .joinInteraction({
          entersektJoinCode: code as string,
          postToParent,
          originRedirectURI: redirectURI,
        })
        .then(() => {
          console.log("joined");
        })
        .catch((err) => {
          console.error(err.toString());
          error.value = `Challenge not found. It has either expired or the link is invalid.`;
        });
    })
    .catch((err) => {
      console.error(err);
      error.value = `Something went wrong: ${err.toString()}`;
    });
}
</script>
